import { useState, useMemo } from "react";
import { v7 as uuidv7 } from "uuid";
import { enqueue, isNetworkError } from "../lib/outbox";
import { Link } from "@tanstack/react-router";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import type { HospitalDto } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { Section, QtyCheckGrid, TextField } from "../components/ChecklistFormBits";
import { SearchableSelect } from "../components/SearchableSelect";
import { buildInitialBagChecklist } from "../lib/initialBagChecklist";
import {
  BAG_FRONT_POCKET,
  BAG_CENTRAL_POCKET,
  BAG_BACK_POCKET,
} from "../lib/bagChecklistFields";
import { CheckCircle2, CloudOff, Loader2, Send, User, LogOut } from "lucide-react";

type PocketKey = "frontPocket" | "centralPocket" | "backPocket";

export function BagChecklistFormPage() {
  const auth = useAuth();
  const api = useApi();
  const { me } = useMe();
  const hospitalsQ = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const hospitals = hospitalsQ.data?.map((h) => ({ ...h, _id: h.id }));
  // One key per submission: retries and offline replays never create duplicates.
  const [submissionKey, setSubmissionKey] = useState(() => uuidv7());
  const submit = (body: unknown) => api.post<{ id: string }>("/api/v1/bag-checklists", body, { "Idempotency-Key": submissionKey });

  const [data, setData] = useState(buildInitialBagChecklist());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<false | "sent" | "queued">(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof typeof data>(k: K, v: (typeof data)[K]) =>
    setData((d) => ({ ...d, [k]: v }));

  const updatePocket = (pocket: PocketKey, key: string, value: boolean) =>
    setData((d) => ({
      ...d,
      [pocket]: { ...(d[pocket] as Record<string, boolean>), [key]: value },
    }));

  const totals = useMemo(() => {
    let total = 0;
    let checked = 0;
    for (const p of ["frontPocket", "centralPocket", "backPocket"] as const) {
      for (const v of Object.values(data[p])) {
        total++;
        if (v) checked++;
      }
    }
    return { total, checked };
  }, [data]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!data.hospitalId) return setError("Please select a hospital");
    if (!data.checkedByName.trim())
      return setError("Please enter who checked the bag");
    if (!data.checkedByBadge.trim())
      return setError("Please enter the badge number");
    setSubmitting(true);
    try {
      const body = {
        hospitalId: data.hospitalId,
        date: data.date,
        shift: data.shift,
        checkedByName: data.checkedByName,
        checkedByBadge: data.checkedByBadge,
        frontPocket: data.frontPocket,
        centralPocket: data.centralPocket,
        backPocket: data.backPocket,
      };
      const saveForLater = async () => {
        await enqueue({ id: submissionKey, kind: "bag-checklist", path: "/api/v1/bag-checklists", body, localFileIds: [], summary: `${data.date} ${data.shift}` });
        setSubmitted("queued");
      };
      if (!navigator.onLine) {
        await saveForLater();
      } else {
        try {
          await submit(body);
          setSubmitted("sent");
        } catch (e) {
          if (!isNetworkError(e)) throw e;
          await saveForLater();
        }
      }
    } catch (err: any) {
      setError(err?.message ?? "Failed to submit bag checklist");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-8 max-w-md w-full text-center">
          {submitted === "queued" ? (
            <CloudOff className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          ) : (
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {submitted === "queued" ? "Bag checklist saved on this device" : "Bag checklist submitted"}
          </h1>
          <p className="text-slate-600 mb-6">
            {submitted === "queued"
              ? "There is no connection right now. It will be sent automatically as soon as the network returns."
              : "Thank you for checking the ACLS bag."}
          </p>
          <div className="flex gap-2 flex-col">
            <button
              onClick={() => {
                setSubmitted(false);
                setSubmissionKey(uuidv7());
                setData(buildInitialBagChecklist());
              }}
              className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold py-2.5 px-4"
            >
              Submit another
            </button>
            <Link
              to="/"
              className="rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 px-4"
            >
              Back to home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src="/hmg-logo.png"
              alt="HMG"
              width={140}
              height={36}
              className="h-7 sm:h-9 w-auto flex-shrink-0"
            />
            <div className="hidden md:block border-l border-slate-300 pl-3">
              <div className="text-xs uppercase tracking-wider text-[var(--hmg-red)] font-semibold">
                EMS
              </div>
              <div className="text-sm font-semibold text-slate-900">
                ACLS Bag Checklist
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div className="text-[10px] sm:text-xs text-slate-500 text-right">
              {totals.checked}/{totals.total} items
            </div>
            {me && (
              <>
                <Link
                  to="/profile"
                  title="My Profile"
                  className="text-slate-600 hover:text-slate-900 p-2 rounded inline-flex items-center gap-1.5 text-sm"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Profile</span>
                </Link>
                <button
                  onClick={() => void auth.signoutRedirect()}
                  title="Sign out"
                  className="text-slate-600 hover:text-red-600 p-2 rounded inline-flex items-center gap-1.5 text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex-1 max-w-3xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6 space-y-4"
      >
        <Section title="Check Information" accent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                Hospital <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={data.hospitalId}
                onChange={(v) => update("hospitalId", v)}
                options={(hospitals ?? []).map((h) => ({
                  value: h._id,
                  label: h.name,
                }))}
                placeholder="Select hospital…"
              />
            </div>
            <TextField
              label="Date"
              type="date"
              required
              value={data.date}
              onChange={(v) => update("date", v)}
            />
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                Shift
              </label>
              <div className="flex gap-2">
                {(["D", "N"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => update("shift", s)}
                    className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${data.shift === s ? "bg-[var(--hmg-red)] text-white border-[var(--hmg-red)]" : "bg-white border-slate-300 text-slate-700"}`}
                  >
                    {s === "D" ? "Day" : "Night"}
                  </button>
                ))}
              </div>
            </div>
            <TextField
              label="Checked by (name)"
              required
              value={data.checkedByName}
              onChange={(v) => update("checkedByName", v)}
            />
            <TextField
              label="Badge No"
              required
              value={data.checkedByBadge}
              onChange={(v) => update("checkedByBadge", v)}
            />
          </div>
        </Section>

        <Section title="Front Pocket">
          <QtyCheckGrid
            items={BAG_FRONT_POCKET}
            values={data.frontPocket}
            onChange={(k, v) => updatePocket("frontPocket", k, v)}
          />
        </Section>
        <Section title="Central Pocket">
          <QtyCheckGrid
            items={BAG_CENTRAL_POCKET}
            values={data.centralPocket}
            onChange={(k, v) => updatePocket("centralPocket", k, v)}
          />
        </Section>
        <Section title="Back Pocket">
          <QtyCheckGrid
            items={BAG_BACK_POCKET}
            values={data.backPocket}
            onChange={(k, v) => updatePocket("backPocket", k, v)}
          />
        </Section>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-4 sticky bottom-3 shadow-lg flex flex-wrap gap-3 items-center justify-between">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">
              {totals.checked}/{totals.total}
            </span>{" "}
            items checked
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Submit Bag Checklist
          </button>
        </div>
      </form>
    </main>
  );
}
